<template>
  <v-card elevation="2">
    <v-card-title class="text-h5">Trim Comparison</v-card-title>
    <v-card-text>
      <v-table fixed-header height="600px">
        <thead>
          <tr>
            <th class="text-left feature-column">Feature</th>
            <th
              v-for="trim in trims"
              :key="trim.id"
              class="text-center trim-column"
            >
              <div class="trim-header">
                <v-img
                  :src="trim.img || noVehicleImage"
                  height="80"
                  contain
                  class="mb-2"
                ></v-img>
                <div class="font-weight-bold">
                  {{ trim.trim || trim.styleName }}
                </div>
                <div class="text-caption">
                  {{ formatCurrency(trim.baseMsrp || trim.price || 0) }}
                </div>
                <v-btn
                  icon
                  size="x-small"
                  variant="text"
                  @click="$emit('remove-trim', trim.id)"
                  class="mt-1"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr class="section-header">
            <td colspan="100%" class="font-weight-bold bg-grey-lighten-4">
              Basic Information
            </td>
          </tr>
          <tr>
            <td class="feature-name">Starting MSRP</td>
            <td
              v-for="trim in trims"
              :key="`price-${trim.id}`"
              class="text-center"
            >
              {{ formatCurrency(trim.baseMsrp || trim.price || 0) }}
            </td>
          </tr>
          <tr>
            <td class="feature-name">Body Style</td>
            <td
              v-for="trim in trims"
              :key="`body-${trim.id}`"
              class="text-center"
            >
              {{ getSpecValue(trim, 'Summary', 'Body Style') }}
            </td>
          </tr>

          <tr class="section-header">
            <td colspan="100%" class="font-weight-bold bg-grey-lighten-4">
              Engine & Performance
            </td>
          </tr>
          <tr>
            <td class="feature-name">Engine Type</td>
            <td
              v-for="trim in trims"
              :key="`engine-${trim.id}`"
              class="text-center"
            >
              {{ getSpecValue(trim, 'Engine', 'Engine Type') }}
            </td>
          </tr>
          <tr>
            <td class="feature-name">Horsepower</td>
            <td
              v-for="trim in trims"
              :key="`hp-${trim.id}`"
              class="text-center"
            >
              {{ getSpecValue(trim, 'Engine', 'SAE Net Horsepower') }}
            </td>
          </tr>
          <tr>
            <td class="feature-name">Torque</td>
            <td
              v-for="trim in trims"
              :key="`torque-${trim.id}`"
              class="text-center"
            >
              {{ getSpecValue(trim, 'Engine', 'SAE Net Torque') }}
            </td>
          </tr>
          <tr>
            <td class="feature-name">Transmission</td>
            <td
              v-for="trim in trims"
              :key="`trans-${trim.id}`"
              class="text-center"
            >
              {{ getSpecValue(trim, 'Transmission', 'Trans Description') }}
            </td>
          </tr>
          <tr>
            <td class="feature-name">Drivetrain</td>
            <td
              v-for="trim in trims"
              :key="`drivetrain-${trim.id}`"
              class="text-center"
            >
              {{ getSpecValue(trim, 'Transmission', 'Drivetrain') }}
            </td>
          </tr>

          <tr class="section-header">
            <td colspan="100%" class="font-weight-bold bg-grey-lighten-4">
              Fuel Economy
            </td>
          </tr>
          <tr>
            <td class="feature-name">City MPG</td>
            <td
              v-for="trim in trims"
              :key="`city-${trim.id}`"
              class="text-center"
            >
              {{ getSpecValue(trim, 'Mileage', 'EPA MPG City') }}
            </td>
          </tr>
          <tr>
            <td class="feature-name">Highway MPG</td>
            <td
              v-for="trim in trims"
              :key="`hwy-${trim.id}`"
              class="text-center"
            >
              {{ getSpecValue(trim, 'Mileage', 'EPA MPG Highway') }}
            </td>
          </tr>
          <tr>
            <td class="feature-name">Combined MPG</td>
            <td
              v-for="trim in trims"
              :key="`combined-${trim.id}`"
              class="text-center"
            >
              {{ getSpecValue(trim, 'Mileage', 'Fuel Economy Est-Combined') }}
            </td>
          </tr>

          <tr class="section-header">
            <td colspan="100%" class="font-weight-bold bg-grey-lighten-4">
              Dimensions
            </td>
          </tr>
          <tr>
            <td class="feature-name">Seating Capacity</td>
            <td
              v-for="trim in trims"
              :key="`seating-${trim.id}`"
              class="text-center"
            >
              {{
                getSpecValue(trim, 'Interior Dimensions', 'Passenger Capacity')
              }}
            </td>
          </tr>
          <tr>
            <td class="feature-name">Front Head Room</td>
            <td
              v-for="trim in trims"
              :key="`fhead-${trim.id}`"
              class="text-center"
            >
              {{ getSpecValue(trim, 'Interior Dimensions', 'Front Head Room') }}
            </td>
          </tr>
          <tr>
            <td class="feature-name">Front Leg Room</td>
            <td
              v-for="trim in trims"
              :key="`fleg-${trim.id}`"
              class="text-center"
            >
              {{ getSpecValue(trim, 'Interior Dimensions', 'Front Leg Room') }}
            </td>
          </tr>
          <tr>
            <td class="feature-name">Cargo Volume</td>
            <td
              v-for="trim in trims"
              :key="`cargo-${trim.id}`"
              class="text-center"
            >
              {{ getSpecValue(trim, 'Interior Dimensions', 'Cargo Volume') }}
            </td>
          </tr>

          <tr class="section-header">
            <td colspan="100%" class="font-weight-bold bg-grey-lighten-4">
              Key Features
            </td>
          </tr>
          <tr v-for="feature in keyFeatures" :key="feature.key">
            <td class="feature-name">{{ feature.name }}</td>
            <td
              v-for="trim in trims"
              :key="`${feature.key}-${trim.id}`"
              class="text-center"
            >
              <v-icon
                v-if="hasFeature(trim, feature.category, feature.searchTerm)"
                color="success"
              >
                mdi-check-circle
              </v-icon>
              <v-icon v-else color="grey-lighten-1"> mdi-minus-circle </v-icon>
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { formatCurrency, getCDNUrl } from '@/utils'
import { parseSpecValue } from '@/utils/modelDetails'
import type { TrimData, ModelData } from '@/types'

const props = defineProps<{
  trims: TrimData[]
  modelData: ModelData | null
}>()

defineEmits<{
  'remove-trim': [trimId: string]
}>()

const cdnUrl = getCDNUrl()
const noVehicleImage = `${cdnUrl}/lightning-model-page/cx/no-vehicle-image.png`

const keyFeatures = [
  {
    key: 'blis',
    name: 'Blind Spot Monitoring',
    category: 'SAFETY',
    searchTerm: 'BLIS',
  },
  {
    key: 'lane',
    name: 'Lane Keeping Assist',
    category: 'SAFETY',
    searchTerm: 'Lane Keeping',
  },
  {
    key: 'adaptive',
    name: 'Adaptive Cruise Control',
    category: 'SAFETY',
    searchTerm: 'Adaptive Cruise',
  },
  {
    key: 'heated',
    name: 'Heated Seats',
    category: 'COMFORT',
    searchTerm: 'Heated',
  },
  {
    key: 'leather',
    name: 'Leather Seats',
    category: 'INTERIOR',
    searchTerm: 'Leather',
  },
  {
    key: 'sunroof',
    name: 'Sunroof/Moonroof',
    category: 'EXTERIOR',
    searchTerm: 'roof',
  },
  {
    key: 'nav',
    name: 'Navigation System',
    category: 'TECHNOLOGY',
    searchTerm: 'Navigation',
  },
  {
    key: 'wireless',
    name: 'Wireless Charging',
    category: 'TECHNOLOGY',
    searchTerm: 'Wireless Charging',
  },
]

const getSpecValue = (
  trim: TrimData,
  category: string,
  searchTerm: string,
): string => {
  const items = trim.technicalSpecification?.[category]?.items || []
  const item = items.find((i: string) => i.includes(searchTerm))
  if (item) {
    return parseSpecValue(item)
  }
  return 'N/A'
}

const hasFeature = (
  trim: TrimData,
  category: string,
  searchTerm: string,
): boolean => {
  const standardItems = trim.standardEquipment?.[category]?.items || []
  const availableItems = trim.availableOptions?.[category]?.items || []
  const allItems = [...standardItems, ...availableItems]
  return allItems.some((item: string) =>
    item.toLowerCase().includes(searchTerm.toLowerCase()),
  )
}
</script>

<style scoped>
.feature-column {
  min-width: 200px;
  position: sticky;
  left: 0;
  background: white;
  z-index: 1;
  border-right: 1px solid #e0e0e0;
}

.trim-column {
  min-width: 200px;
}

.trim-header {
  padding: 8px;
}

.feature-name {
  font-weight: 500;
  padding: 12px 16px;
  background: white;
  position: sticky;
  left: 0;
  z-index: 1;
  border-right: 1px solid #e0e0e0;
}

.section-header td {
  padding: 12px 16px;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

tbody tr:hover {
  background-color: #f5f5f5;
}

:deep(.v-table__wrapper) {
  border: 1px solid #e0e0e0;
}
</style>
